/**
 * TPWorkbookUpload
 *
 * Shared UI component for the TP workbook upload step.
 * Used in GenerateTPPage.
 *
 * Renders:
 *   - Upload button (triggers hidden <input type="file">)
 *   - Clear button when a file is loaded
 *   - Parse error display
 *   - Parse success summary (sections, 4.11 SR rows, warnings, unsupported)
 */
import React from 'react'
import { Button } from '@/components/ui/Button'
import { Upload, CheckCircle, AlertTriangle } from 'lucide-react'
import type { UseTPWorkbookReturn } from '@/hooks/useTPWorkbook'
import { useTranslation } from 'react-i18next'

interface TPWorkbookUploadProps {
  hook: UseTPWorkbookReturn
  /** When true, omits the label header (useful inside compact modals). */
  compact?: boolean
}

export const TPWorkbookUpload: React.FC<TPWorkbookUploadProps> = ({ hook, compact = false }) => {
  const { t } = useTranslation()
  const {
    workbookInputRef,
    workbookResult,
    workbookFileName,
    workbookParsing,
    workbookError,
    handleWorkbookUpload,
    clearWorkbook,
  } = hook

  return (
    <div className="space-y-2">
      {!compact && (
        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('generateTP.workbook')} <span className="text-red-500">*</span>
          <span className="font-normal text-slate-400 ml-1">{t('generateTP.workbookRequired')}</span>
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => workbookInputRef.current?.click()}
          disabled={workbookParsing}
        >
          <Upload className="h-4 w-4 mr-2" />
          {workbookParsing ? t('generateTP.parsing') : workbookFileName ? t('generateTP.replaceWorkbook') : t('generateTP.uploadWorkbook')}
        </Button>

        {workbookFileName && !workbookError && (
          <button
            type="button"
            onClick={clearWorkbook}
            className="text-xs text-slate-400 hover:text-red-500"
          >
            ✕ {t('common.clear')}
          </button>
        )}

        <input
          ref={workbookInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleWorkbookUpload}
        />
      </div>

      {workbookError && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {workbookError}
        </p>
      )}

      {workbookResult && !workbookError && (
        <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 text-xs space-y-1">
          <p className="text-green-700 dark:text-green-300 font-medium flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> {workbookFileName}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-600 dark:text-slate-400">
            <span>{t('generateTP.sectionsParsed')}</span>
            <span className="font-medium text-slate-800 dark:text-slate-200">
              {Object.keys(workbookResult.sections).length}
            </span>
            <span>{t('generateTP.srRows')}</span>
            <span className="font-medium text-slate-800 dark:text-slate-200">
              {workbookResult.comm411RowCount}
            </span>
            <span>{t('generateTP.warnings')}</span>
            <span className={`font-medium ${workbookResult.warnings.length > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-slate-200'}`}>
              {workbookResult.warnings.length}
            </span>
            <span>{t('generateTP.unsupportedRows')}</span>
            <span className={`font-medium ${workbookResult.unsupported.length > 0 ? 'text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>
              {workbookResult.unsupported.length}
            </span>
          </div>
          {workbookResult.warnings.length > 0 && (
            <details className="cursor-pointer pt-0.5">
              <summary className="text-amber-600 dark:text-amber-400 select-none">{t('generateTP.viewWarnings')}</summary>
              <ul className="mt-1 pl-3 space-y-0.5 text-slate-500 dark:text-slate-400">
                {workbookResult.warnings.map((w, i) => <li key={i}>• {w}</li>)}
              </ul>
            </details>
          )}
          {workbookResult.unsupported.length > 0 && (
            <p className="text-slate-400 pt-0.5">
              {t('generateTP.skipped')}: {workbookResult.unsupported.join('; ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
